import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Customer, EntityMeta, Phone, Staff } from '@qtek/shared/models';
import { Observable, of, pipe } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { StaffService } from './staff.service';

export interface RelationsStoreState {
  customers: Customer[];
  staff: Staff[];
  isStaffLoading: boolean;
  staffMeta: EntityMeta | null;
}

const initialState: RelationsStoreState = {
  customers: [],
  staff: [],
  isStaffLoading: false,
  staffMeta: null,
};

export const RelationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(StaffService)) => ({
    getStaff: rxMethod<{
      params?: {
        includeMyself?: boolean;
        excludeAlreadyInvitedToItemId?: boolean;
      };
    }>(
      pipe(
        tap(() => patchState(store, { isStaffLoading: true })),
        switchMap(({ params }) =>
          service.getStaff(params).pipe(
            tap(({ res, sts }) =>
              patchState(store, {
                staff: res || [],
                isStaffLoading: false,
                staffMeta: sts?.meta || null,
              })
            ),
            catchError(() => {
              patchState(store, { isStaffLoading: false });
              return of(null);
            })
          )
        )
      )
    ),

    /** Emits the created staff member on success so callers can close their dialog. */
    createEmployee(payload: Staff): Observable<Staff> {
      return service.createEmployee(payload).pipe(
        map(({ res }) => res),
        tap(res =>
          patchState(store, state => ({ staff: [res, ...state.staff] }))
        )
      );
    },

    /** Emits the updated staff member on success; re-throws on failure. */
    updateEmployee(form: Staff, usrId: string): Observable<Staff> {
      return service.updateStaff(form, usrId).pipe(
        map(({ res }) => res),
        tap(res =>
          patchState(store, state => {
            const staff = state.staff.slice();
            const index = staff.findIndex(
              relation =>
                relation._id === usrId ||
                (!!res.personId && relation.personId === res.personId)
            );
            if (index !== -1) {
              staff[index] = { ...staff[index], ...res };
            }
            return { staff };
          })
        )
      );
    },

    deleteEmployee(id: string): Observable<unknown> {
      return service.deleteStaff(id).pipe(
        tap(() =>
          patchState(store, state => ({
            staff: state.staff.filter(relation => relation._id !== id),
          }))
        )
      );
    },

    updateStaffPhone(payload: {
      userId: string;
      phone: Phone;
    }): Observable<unknown> {
      return service.updateStaffPhone(payload);
    },

    sendStaffInvitation(userId: string): Observable<unknown> {
      return service.sendInvitation(userId);
    },
  }))
);
